<script lang="ts">
  import { router, page, Link } from '@inertiajs/svelte';
  import momentiumLogo from '../assets/momentium-logo.png';
  import ProfileDropdown from '../components/ProfileDropdown.svelte';
  import MobileTopBar from '../components/mobile/MobileTopBar.svelte';
  import { viewport } from '../lib/stores/viewport';
  import Icon from '../components/Icon.svelte';
  import type { FavoriteView, NavbarConfig, View, FavoritePageType } from '../lib/types';
  import { getFavoritePageType, CALENDAR_LAYOUT_LABELS } from '../lib/types';
  import { DEFAULT_NAVBAR_VIEWS, MAX_NAVBAR_VIEWS, MIN_NAVBAR_VIEWS } from '../lib/types';
  import { useModalAnimation } from '../lib/useModalAnimation.svelte';
  import {
    favoriteViews,
    navbarConfig,
    editFavorite,
    removeFavorite,
    reorderFavorites,
    setNavbarConfig,
  } from '../lib/stores/favorites';
  import { nowState, nowDensity, saveNowPreferences, setNowDensity } from '../lib/stores/now';
  import { NOW_DENSITIES, type NowDensity } from '../lib/types';
  import { mobileTabs, saveMobileTabs } from '../lib/stores/mobileTabs';
  import {
    MOBILE_TAB_KEYS,
    MOBILE_TAB_LABELS,
    setMobileTabSlot,
    type MobileTabKey,
  } from '../lib/mobileTabs';
  import { toast } from 'svelte-sonner';

  interface TimezoneOption {
    name: string;
    offset: string;
  }

  interface ApiKeyInfo {
    id: string;
    name: string;
    lastUsedAt: string | null;
    lastUsedAgo: string | null;
    createdAt: string;
  }

  interface Props {
    timezone: string;
    availableTimezones: TimezoneOption[];
    apiKeys: ApiKeyInfo[];
    newApiKeyToken?: string | null;
  }

  let {
    timezone,
    availableTimezones,
    apiKeys,
    newApiKeyToken: newApiKeyTokenProp,
  }: Props = $props();

  let auth = $derived(
    $page.props.auth as { user: { id: number; email_address: string } | null } | undefined,
  );
  let currentUser = $derived(auth?.user);

  // === Mobile tabs ===
  // Picking a tab already in another slot swaps the two, so the set stays valid.
  // Slots are disabled while a save runs so saves never overlap.
  let isSavingMobileTabs = $state(false);

  async function changeMobileTab(index: number, key: MobileTabKey) {
    if (isSavingMobileTabs) return;
    isSavingMobileTabs = true;
    try {
      await saveMobileTabs(setMobileTabSlot($mobileTabs, index, key));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save mobile tabs');
    } finally {
      isSavingMobileTabs = false;
    }
  }

  // === Timezone ===
  let selectedTimezone = $derived(timezone);
  let isSubmitting = $state(false);
  let hasChanges = $derived(selectedTimezone !== timezone);

  function handleSubmit(e: Event) {
    e.preventDefault();
    if (!hasChanges || isSubmitting) return;
    isSubmitting = true;
    router.patch(
      '/settings',
      {
        user: { timezone: selectedTimezone },
      },
      {
        onFinish: () => {
          isSubmitting = false;
        },
      },
    );
  }

  // === API Keys ===
  let newKeyName = $state('');
  let isCreatingKey = $state(false);
  let confirmRevokeKey: ApiKeyInfo | null = $state(null);

  // Token passed as Inertia prop after creation (shown once, consumed from session)
  let newApiKeyToken: string | null = $derived(newApiKeyTokenProp ?? null);

  let tokenCopied = $state(false);

  function handleCreateKey(e: Event) {
    e.preventDefault();
    if (!newKeyName.trim() || isCreatingKey) return;
    isCreatingKey = true;
    router.post(
      '/settings/create_api_key',
      { api_key: { name: newKeyName.trim() } },
      {
        preserveScroll: true,
        onFinish: () => {
          isCreatingKey = false;
          newKeyName = '';
        },
      },
    );
  }

  async function copyToken() {
    if (!newApiKeyToken) return;

    // Select the token text for visual feedback
    const tokenEl = document.querySelector('.token-value');
    if (tokenEl) {
      const range = document.createRange();
      range.selectNodeContents(tokenEl);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    try {
      await navigator.clipboard.writeText(newApiKeyToken);
    } catch {
      // Fallback for non-HTTPS / older browsers
      const textarea = document.createElement('textarea');
      textarea.value = newApiKeyToken;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    tokenCopied = true;
    setTimeout(() => {
      tokenCopied = false;
      // Clear selection after the copied state fades
      window.getSelection()?.removeAllRanges();
    }, 2000);
  }

  function requestRevoke(key: ApiKeyInfo) {
    confirmRevokeKey = key;
  }

  function cancelRevoke() {
    confirmRevokeKey = null;
  }

  function confirmRevoke() {
    if (!confirmRevokeKey) return;
    const id = confirmRevokeKey.id;
    confirmRevokeKey = null;
    router.delete(`/settings/api_keys/${id}`, { preserveScroll: true });
  }

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function formatLastUsed(iso: string | null): string {
    if (!iso) return 'Never';
    return formatDate(iso);
  }

  // === Favorites ===
  let favorites = $derived($favoriteViews);
  let sortedFavorites = $derived([...favorites].sort((a, b) => a.position - b.position));

  let currentNavbarConfig = $derived($navbarConfig);

  // Editing state
  let editingId: string | null = $state(null);
  let editName = $state('');
  let editIcon = $state('');

  function startEdit(fav: FavoriteView) {
    editingId = fav.id;
    editName = fav.name;
    editIcon = fav.icon || '';
  }

  async function saveEdit() {
    if (!editingId || !editName.trim()) return;
    await editFavorite(editingId, { name: editName.trim(), icon: editIcon || undefined });
    editingId = null;
  }

  function cancelEdit() {
    editingId = null;
  }

  let confirmDeleteId: string | null = $state(null);

  function requestRemove(id: string) {
    confirmDeleteId = id;
  }

  function cancelRemove() {
    confirmDeleteId = null;
  }

  async function confirmRemove() {
    if (!confirmDeleteId) return;
    await removeFavorite(confirmDeleteId);
    confirmDeleteId = null;
  }

  // Drag-and-drop reordering
  let dragIdx: number | null = $state(null);
  let dragOverIdx: number | null = $state(null);

  function onDragStart(e: DragEvent, idx: number) {
    dragIdx = idx;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(idx));
    }
  }

  function onDragOver(e: DragEvent, idx: number) {
    e.preventDefault();
    dragOverIdx = idx;
  }

  function onDragLeave() {
    dragOverIdx = null;
  }

  async function onDrop(e: DragEvent, targetIdx: number) {
    e.preventDefault();
    dragOverIdx = null;
    if (dragIdx === null || dragIdx === targetIdx) {
      dragIdx = null;
      return;
    }

    const items = [...sortedFavorites];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(targetIdx, 0, moved);
    const orderedIds = items.map((f) => f.id);
    dragIdx = null;
    await reorderFavorites(orderedIds);
  }

  function onDragEnd() {
    dragIdx = null;
    dragOverIdx = null;
  }

  // === Navbar Config: View Selection (1-3 views as top-level buttons) ===
  const allViews: { key: View; label: string; icon: string }[] = [
    { key: 'days', label: 'Days', icon: '📆' },
    { key: 'rolling', label: 'Rolling', icon: '📜' },
    { key: 'focus', label: 'Focus', icon: '🎯' },
    { key: 'table', label: 'Table', icon: '☰' },
    { key: 'timeline', label: 'Timeline', icon: '📊' },
    { key: 'gantt', label: 'Gantt', icon: '📈' },
    { key: 'grid-6x2', label: 'Grid 6×2', icon: '⊞' },
    { key: 'grid-quarters', label: 'Quarters', icon: '▥' },
  ];

  // Current navbar views from config
  let selectedNavbarViews = $derived(currentNavbarConfig.navbar_views || DEFAULT_NAVBAR_VIEWS);

  function isViewSelected(viewKey: string): boolean {
    return selectedNavbarViews.includes(viewKey);
  }

  // Validation warning
  let navbarWarning: string | null = $state(null);
  let navbarWarningTimer: ReturnType<typeof setTimeout> | null = null;

  function showNavbarWarning(msg: string) {
    navbarWarning = msg;
    if (navbarWarningTimer) clearTimeout(navbarWarningTimer);
    navbarWarningTimer = setTimeout(() => {
      navbarWarning = null;
    }, 3000);
  }

  async function toggleNavbarView(viewKey: string) {
    const current = [...selectedNavbarViews];
    const isSelected = current.includes(viewKey);

    if (isSelected) {
      // Removing — must keep at least MIN_NAVBAR_VIEWS
      if (current.length <= MIN_NAVBAR_VIEWS) {
        showNavbarWarning(`At least ${MIN_NAVBAR_VIEWS} view must be in the navbar.`);
        return false;
      }
      const newViews = current.filter((v) => v !== viewKey);
      navbarWarning = null;
      await setNavbarConfig({ navbar_views: newViews });
      return true;
    } else {
      // Adding — must not exceed MAX_NAVBAR_VIEWS
      if (current.length >= MAX_NAVBAR_VIEWS) {
        showNavbarWarning(`Maximum ${MAX_NAVBAR_VIEWS} views can be shown in the navbar.`);
        return false;
      }
      const newViews = [...current, viewKey];
      navbarWarning = null;
      await setNavbarConfig({ navbar_views: newViews });
      return true;
    }
  }

  // View labels for favorites display
  const scaleLabels: Record<string, string> = { month: 'Month', week: 'Week', year: 'Year' };
  const viewLabelMap: Record<string, string> = {
    focus: 'Focus',
    rolling: 'Rolling',
    days: 'Days',
    table: 'Table',
    timeline: 'Timeline',
    gantt: 'Gantt',
    'grid-6x2': 'Grid 6×2',
    'grid-quarters': 'Quarters',
  };

  const rangeLabelMap: Record<string, string> = {
    '1-month': '1 Month',
    quarter: 'Quarter',
    '3-months': '3 Months',
    '6-months': '6 Months',
    year: 'Year',
    '1-week': '1 Week',
    month: 'Month',
    '5': '5 Weeks',
    '7': '7 Weeks',
    full: 'Full Year',
    half: 'Half Year',
  };

  function configSummaryText(fav: FavoriteView): string {
    if (getFavoritePageType(fav) === 'calendar') {
      const parts = ['Calendar'];
      if (fav.layout) parts.push(CALENDAR_LAYOUT_LABELS[fav.layout] || fav.layout);
      return parts.join(' · ');
    }
    const parts = [viewLabelMap[fav.view!] || fav.view, scaleLabels[fav.scale!] || fav.scale];
    if (fav.range) parts.push(rangeLabelMap[fav.range as string] || String(fav.range));
    if (fav.display && fav.display !== 'both') parts.push(fav.display);
    return parts.join(' · ');
  }

  const commonIcons = ['⭐', '🏃', '📋', '🗓️', '🎯', '📊', '🔥', '💼', '🏠', '🌟', '📌', '🚀'];

  // Preview: compute what navbar would show
  let previewButtons = $derived(selectedNavbarViews);
  let previewMoreViews = $derived(allViews.filter((v) => !selectedNavbarViews.includes(v.key)));
  let previewHasMore = $derived(previewMoreViews.length > 0);
</script>

<div class="settings-wrapper app-min-width">
  <!-- Simple header for settings page -->
  {#if $viewport.isMobile}
    <MobileTopBar title="Settings" back="/more" backLabel="Back to More" />
  {:else}
    <header class="settings-header">
      <div class="header-left">
        <Link href="/" class="logo-link">
          <img src={momentiumLogo} alt="Momentium" class="logo" />
          <span class="logo-text">Momentium</span>
        </Link>
        <span class="breadcrumb">/ Settings</span>
      </div>
      <div class="header-right">
        <ProfileDropdown />
      </div>
    </header>
  {/if}

  <main class="settings-page">
    <div class="settings-container">
      <h1 class="settings-title">Settings</h1>

      <!-- Timezone Section -->
      <form onsubmit={handleSubmit}>
        <section class="settings-section">
          <h2 class="section-title">Time Zone</h2>
          <p class="section-description">
            Your timezone affects how event dates and times are displayed.
          </p>

          <div class="field">
            <label for="timezone-select" class="field-label">Timezone</label>
            <select id="timezone-select" bind:value={selectedTimezone} class="field-select">
              {#each availableTimezones as tz}
                <option value={tz.name}>
                  (UTC{tz.offset}) {tz.name}
                </option>
              {/each}
            </select>
          </div>

          <div class="actions">
            <button type="submit" class="save-btn" disabled={!hasChanges || isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </section>
      </form>

      <!-- Now Section -->
      <section class="settings-section">
        <h2 class="section-title">Now</h2>
        <p class="section-description">
          A small daily commitment list. Tasks stay until you finish or remove them, and keep a
          day-count while they linger. Completing a task keeps the slot until tomorrow unless you
          allow same-day refill.
        </p>

        <div class="field">
          <label for="now-density" class="field-label">Size</label>
          <select
            id="now-density"
            class="field-select"
            aria-describedby="now-density-help"
            value={$nowDensity}
            onchange={(e) => setNowDensity(e.currentTarget.value as NowDensity)}
          >
            {#each NOW_DENSITIES as option}
              <option value={option}>{option[0].toUpperCase() + option.slice(1)}</option>
            {/each}
          </select>
          <p id="now-density-help" class="field-help">
            Applies on desktop; on phones use the Now tab
          </p>
        </div>

        <div class="field">
          <label for="now-capacity" class="field-label">Capacity (1–7)</label>
          <input
            autocomplete="off"
            id="now-capacity"
            class="field-select"
            type="number"
            min="1"
            max="7"
            value={$nowState.capacity}
            onchange={(e) => saveNowPreferences({ capacity: Number(e.currentTarget.value) })}
          />
        </div>

        <label class="now-refill">
          <input
            type="checkbox"
            checked={$nowState.refillSameDay}
            onchange={(e) => saveNowPreferences({ refillSameDay: e.currentTarget.checked })}
          />
          Allow adding another task the same day after completing one
        </label>
      </section>

      <!-- Mobile Tabs Section -->
      <section class="settings-section">
        <h2 class="section-title">Mobile tabs</h2>
        <p class="section-description">
          Choose the four tabs in the bottom bar on your phone. <strong>More</strong> is always last and
          holds everything else.
        </p>

        <div class="mobile-tab-slots">
          {#each $mobileTabs as tabKey, index (index)}
            <div class="mobile-tab-slot">
              <label for="mobile-tab-{index}" class="field-label">Tab {index + 1}</label>
              <select
                id="mobile-tab-{index}"
                class="field-select"
                value={tabKey}
                disabled={isSavingMobileTabs}
                onchange={(e) => changeMobileTab(index, e.currentTarget.value as MobileTabKey)}
              >
                {#each MOBILE_TAB_KEYS as key (key)}
                  <option value={key}>{MOBILE_TAB_LABELS[key]}</option>
                {/each}
              </select>
            </div>
          {/each}
        </div>
      </section>

      <!-- API Keys Section -->
      <section class="settings-section">
        <h2 class="section-title">API Keys</h2>
        <p class="section-description">
          Manage API keys for CLI tools, AI agents, and third-party integrations.
        </p>

        <!-- New token display (shown once after creation) -->
        {#if newApiKeyToken}
          <div class="token-reveal">
            <div class="token-reveal-header">
              <span class="token-reveal-icon">🔑</span>
              <strong>Your new API key</strong>
            </div>
            <div class="token-reveal-body">
              <code class="token-value" class:token-highlight={tokenCopied}>{newApiKeyToken}</code>
              <div class="copy-btn-wrapper">
                <button
                  class="copy-btn"
                  class:copy-btn-success={tokenCopied}
                  onclick={copyToken}
                  type="button"
                >
                  {#if tokenCopied}
                    <Icon name="check" size={13} strokeWidth={2} />Copied!
                  {:else}
                    <Icon name="duplicate" size={13} />Copy
                  {/if}
                </button>
                {#if tokenCopied}
                  <div class="copy-toast">Copied to clipboard</div>
                {/if}
              </div>
            </div>
            <p class="token-warning">
              ⚠ This token will not be shown again. Copy it now and store it securely.
            </p>
          </div>
        {/if}

        <!-- Create new key -->
        <form class="create-key-form" onsubmit={handleCreateKey}>
          <input
            autocomplete="off"
            class="key-name-input"
            type="text"
            placeholder="Key name (e.g., CLI, CI/CD)"
            bind:value={newKeyName}
            maxlength="100"
          />
          <button
            class="create-key-btn"
            type="submit"
            disabled={!newKeyName.trim() || isCreatingKey}
          >
            {isCreatingKey ? 'Creating...' : 'Create new key'}
          </button>
        </form>

        <!-- Key list -->
        {#if apiKeys.length === 0 && !newApiKeyToken}
          <div class="empty-state">
            <span class="empty-icon">🔑</span>
            <p class="empty-text">No API keys yet.</p>
            <p class="empty-hint">Create one to use with the CLI or integrations.</p>
          </div>
        {:else if apiKeys.length > 0}
          <div class="api-keys-list">
            <div class="api-keys-header">
              <span class="ak-col-name">Name</span>
              <span class="ak-col-created">Created</span>
              <span class="ak-col-used">Last Used</span>
              <span class="ak-col-action"></span>
            </div>
            {#each apiKeys as key (key.id)}
              <div class="api-key-row">
                <span class="ak-col-name ak-name">{key.name}</span>
                <span class="ak-col-created ak-date">{formatDate(key.createdAt)}</span>
                <span class="ak-col-used ak-date">
                  {formatLastUsed(key.lastUsedAt)}
                  {#if key.lastUsedAgo}
                    <span class="ak-time-ago">({key.lastUsedAgo})</span>
                  {/if}
                </span>
                <span class="ak-col-action">
                  <button
                    class="revoke-btn"
                    onclick={() => requestRevoke(key)}
                    type="button"
                    title="Revoke this key">Revoke</button
                  >
                </span>
              </div>
            {/each}
          </div>
        {/if}
      </section>

      <!-- Navigation & Views Section -->
      <section class="settings-section views-section">
        <h2 class="section-title">Navigation & Views</h2>
        <p class="section-description">
          Customize your navbar view buttons and manage favorite view configurations.
        </p>

        <!-- Favorite Views -->
        <div class="subsection">
          <h3 class="subsection-title">
            <span class="subsection-icon">★</span> Favorite Views
          </h3>
          <p class="subsection-description">
            Quick access to your preferred view configurations. Save favorites using the ☆ button in
            the navbar on Timeline or Calendar pages, or press <kbd>1</kbd>–<kbd>9</kbd> to jump to
            a favorite from either page. The
            <strong>#1 favorite</strong> becomes your default view when navigating to the home page.
          </p>

          {#if sortedFavorites.length === 0}
            <div class="empty-state">
              <span class="empty-icon">☆</span>
              <p class="empty-text">No favorite views yet.</p>
              <p class="empty-hint">
                Click the ☆ button in the navbar to save your current view configuration as a
                favorite.
              </p>
            </div>
          {:else}
            <div class="favorites-list">
              {#each sortedFavorites as fav, idx (fav.id)}
                <div
                  class="favorite-row"
                  class:dragging={dragIdx === idx}
                  class:drag-over={dragOverIdx === idx}
                  draggable="true"
                  ondragstart={(e) => onDragStart(e, idx)}
                  ondragover={(e) => onDragOver(e, idx)}
                  ondragleave={onDragLeave}
                  ondrop={(e) => onDrop(e, idx)}
                  ondragend={onDragEnd}
                  role="listitem"
                >
                  <span class="drag-handle" title="Drag to reorder">≡</span>
                  <span class="fav-number">{idx + 1}</span>

                  {#if editingId === fav.id}
                    <!-- Editing mode -->
                    <div class="fav-edit-form">
                      <div class="fav-edit-row">
                        <div class="icon-picker-inline">
                          {#each commonIcons as icon}
                            <button
                              class="icon-option-sm"
                              class:selected={editIcon === icon}
                              onclick={() => (editIcon = editIcon === icon ? '' : icon)}
                              type="button">{icon}</button
                            >
                          {/each}
                        </div>
                      </div>
                      <div class="fav-edit-row">
                        <input
                          autocomplete="off"
                          class="fav-edit-input"
                          type="text"
                          bind:value={editName}
                          maxlength="50"
                          onkeydown={(e) => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                        <button class="fav-edit-save" onclick={saveEdit} type="button">Save</button>
                        <button class="fav-edit-cancel" onclick={cancelEdit} type="button"
                          >Cancel</button
                        >
                      </div>
                    </div>
                  {:else}
                    <!-- Display mode -->
                    <span class="fav-icon-display">{fav.icon || '⭐'}</span>
                    <div class="fav-info">
                      <span class="fav-name">{fav.name}</span>
                      <div class="fav-badges">
                        {#if getFavoritePageType(fav) === 'calendar'}
                          <span class="fav-badge">Page: Calendar</span>
                          {#if fav.layout}
                            <span class="fav-badge"
                              >Layout: {CALENDAR_LAYOUT_LABELS[fav.layout] || fav.layout}</span
                            >
                          {/if}
                        {:else}
                          <span class="fav-badge">View: {viewLabelMap[fav.view!] || fav.view}</span>
                          <span class="fav-badge"
                            >Scale: {scaleLabels[fav.scale!] || fav.scale}</span
                          >
                          {#if fav.range}
                            <span class="fav-badge"
                              >Range: {rangeLabelMap[fav.range as string] || fav.range}</span
                            >
                          {/if}
                          {#if fav.display && fav.display !== 'both'}
                            <span class="fav-badge">Show: {fav.display}</span>
                          {/if}
                        {/if}
                      </div>
                    </div>
                    <div class="fav-actions">
                      <button class="fav-action-btn" onclick={() => startEdit(fav)} title="Edit"
                        >✎</button
                      >
                      <button
                        class="fav-action-btn danger"
                        onclick={() => requestRemove(fav.id)}
                        title="Remove">✕</button
                      >
                    </div>
                    {#if confirmDeleteId === fav.id}
                      <div class="fav-confirm-delete">
                        <span class="fav-confirm-text">Delete "{fav.name}"?</span>
                        <button class="fav-confirm-btn danger" onclick={confirmRemove} type="button"
                          >Delete</button
                        >
                        <button class="fav-confirm-btn" onclick={cancelRemove} type="button"
                          >Cancel</button
                        >
                      </div>
                    {/if}
                  {/if}
                </div>
              {/each}
            </div>
          {/if}
        </div>

        <!-- Navbar View Buttons -->
        <div class="subsection">
          <h3 class="subsection-title">
            <span class="subsection-icon">☰</span> Navbar View Buttons
          </h3>
          <p class="subsection-description">
            Choose up to {MAX_NAVBAR_VIEWS} views to show as quick-access buttons in the navbar. All other
            views are available in the "More" dropdown.
          </p>

          <div class="view-toggles">
            {#each allViews as v}
              {@const selected = isViewSelected(v.key)}
              <button
                class="view-toggle"
                class:view-toggle-selected={selected}
                class:view-toggle-disabled={!selected &&
                  selectedNavbarViews.length >= MAX_NAVBAR_VIEWS}
                onclick={() => toggleNavbarView(v.key)}
                type="button"
              >
                {#if selected}
                  <span class="view-toggle-check">✓</span>
                {:else}
                  <span class="view-toggle-check empty"></span>
                {/if}
                <span class="view-toggle-icon">{v.icon}</span>
                <span class="view-toggle-label">{v.label}</span>
              </button>
            {/each}
          </div>

          <div class="view-count-hint">
            {selectedNavbarViews.length} of {MAX_NAVBAR_VIEWS} selected
          </div>

          {#if navbarWarning}
            <div class="navbar-warning">
              <span class="navbar-warning-icon">⚠</span>
              <span class="navbar-warning-text">{navbarWarning}</span>
            </div>
          {/if}

          <!-- Live Preview -->
          <div class="navbar-preview">
            <span class="preview-label">Preview:</span>
            <div class="preview-bar">
              {#if sortedFavorites.length > 0}
                <span class="preview-btn preview-fav">★ Favorites ▾</span>
              {/if}
              <div class="preview-view-btns">
                {#each previewButtons as v}
                  {@const info = allViews.find((av) => av.key === v)}
                  {#if info}
                    <span class="preview-btn">{info.icon} {info.label}</span>
                  {/if}
                {/each}
                {#if previewHasMore}
                  <span class="preview-btn">⋯ More</span>
                {/if}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>
</div>

<!-- Revoke API Key Confirmation Modal -->
{#if confirmRevokeKey}
  {@const revokeModal = useModalAnimation(() => !!confirmRevokeKey, cancelRevoke)}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="modal-overlay"
    class:closing={revokeModal.closing}
    bind:this={revokeModal.overlayEl}
    onclick={revokeModal.animateClose}
    role="presentation"
  >
    <div class="modal-card" onclick={(e) => e.stopPropagation()} role="dialog" tabindex="-1">
      <div class="modal-header">
        <span class="modal-header-icon">⚠️</span>
        <h2 class="modal-title">Revoke API Key</h2>
      </div>

      <div class="modal-body">
        <p class="modal-key-name">"{confirmRevokeKey.name}"</p>
        {#if confirmRevokeKey.lastUsedAt}
          <p class="modal-last-used">
            Last used {formatDate(confirmRevokeKey.lastUsedAt)} ({confirmRevokeKey.lastUsedAgo})
          </p>
        {:else}
          <p class="modal-last-used">Never used</p>
        {/if}
        <p class="modal-text">
          This action is <strong>permanent and cannot be undone.</strong>
        </p>
        <div class="modal-implications">
          <p class="modal-implications-title">Revoking this key will:</p>
          <ul class="modal-implications-list">
            <li>Immediately invalidate the key</li>
            <li>Break any CLI tools, scripts, or integrations currently using it</li>
            <li>Cause API requests with this key to return authentication errors</li>
          </ul>
        </div>
        <p class="modal-text modal-hint">
          If you're rotating keys, create a new key and update your clients <em>before</em> revoking this
          one.
        </p>
      </div>

      <div class="modal-actions">
        <button type="button" class="modal-btn modal-btn-cancel" onclick={revokeModal.animateClose}>
          Cancel
        </button>
        <button type="button" class="modal-btn modal-btn-danger" onclick={confirmRevoke}>
          Revoke Key
        </button>
      </div>
    </div>
  </div>
{/if}

<svelte:window
  onkeydown={confirmRevokeKey
    ? (e) => {
        if (e.key === 'Escape') cancelRevoke();
      }
    : undefined}
/>

<style>
  .settings-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  .settings-page {
    /* Fill the flex-column wrapper: auto margins disable align-self: stretch,
       which let the page size to its widest row and overflow on phones. */
    width: 100%;
    min-width: 0;
    padding: 2rem;
    max-width: 600px;
    margin: 0 auto;
  }

  .settings-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 2rem;
  }

  .settings-section {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.25rem;
  }

  .section-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin-bottom: 1.25rem;
  }

  .now-refill {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #374151;
  }

  .field {
    margin-bottom: 1.25rem;
  }

  .field-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.375rem;
  }

  .field-select {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    color: #111827;
    background: white;
    outline: none;
    transition: border-color 0.15s;
  }

  .field-help {
    margin-top: 0.375rem;
    font-size: 0.75rem;
    color: #6b7280;
  }

  .field-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }

  .actions {
    display: flex;
    justify-content: flex-end;
  }

  .mobile-tab-slots {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: 0.75rem;
  }

  .save-btn {
    padding: 0.5rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .save-btn:hover:not(:disabled) {
    background: #2563eb;
  }

  .save-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .settings-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    background: white;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :global(.logo-link) {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-shrink: 0;
    text-decoration: none;
  }

  .logo {
    height: 1.5rem;
    flex-shrink: 0;
  }

  .logo-text {
    font-size: 0.875rem;
    font-weight: 700;
    color: #111827;
  }

  .breadcrumb {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  /* === Navigation & Views Section === */

  .views-section {
    margin-top: 0;
  }

  .subsection {
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .subsection:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  .subsection-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .subsection-icon {
    font-size: 0.875rem;
    color: #f59e0b;
  }

  .subsection-description {
    font-size: 0.8125rem;
    color: #6b7280;
    margin-bottom: 0.75rem;
    line-height: 1.5;
  }

  .subsection-description kbd {
    display: inline-block;
    padding: 0.0625rem 0.25rem;
    font-size: 0.6875rem;
    font-family: monospace;
    color: #374151;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 0.1875rem;
  }

  /* Empty state */
  .empty-state {
    text-align: center;
    padding: 1.25rem;
    background: #f9fafb;
    border-radius: 0.5rem;
    border: 1px dashed #d1d5db;
  }

  .empty-icon {
    font-size: 1.5rem;
    display: block;
    margin-bottom: 0.375rem;
    opacity: 0.4;
  }

  .empty-text {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 0.25rem;
  }

  .empty-hint {
    font-size: 0.75rem;
    color: #9ca3af;
  }

  /* Favorites list */
  .favorites-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .favorite-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.625rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    transition: all 0.15s;
  }

  .favorite-row.dragging {
    opacity: 0.4;
  }

  .favorite-row.drag-over {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  .drag-handle {
    font-size: 0.875rem;
    color: #9ca3af;
    cursor: grab;
    user-select: none;
    flex-shrink: 0;
    line-height: 1;
  }

  .drag-handle:active {
    cursor: grabbing;
  }

  .fav-number {
    font-size: 0.625rem;
    font-weight: 700;
    color: #9ca3af;
    background: #e5e7eb;
    width: 1.125rem;
    height: 1.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.1875rem;
    flex-shrink: 0;
  }

  .fav-icon-display {
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  .fav-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
  }

  .fav-name {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .fav-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.1875rem;
    margin-top: 0.125rem;
  }

  .fav-badge {
    font-size: 0.5625rem;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 0.1875rem;
    padding: 0 0.25rem;
    line-height: 1.5;
    white-space: nowrap;
  }

  .fav-actions {
    display: flex;
    gap: 0.25rem;
    flex-shrink: 0;
  }

  .fav-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.375rem;
    height: 1.375rem;
    font-size: 0.6875rem;
    color: #6b7280;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
    padding: 0;
  }

  .fav-action-btn:hover {
    color: #374151;
    background: #e5e7eb;
    border-color: #d1d5db;
  }

  .fav-confirm-delete {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    margin-top: 0.375rem;
    padding-top: 0.375rem;
    border-top: 1px solid #fecaca;
    background: #fef2f2;
    margin-left: -0.625rem;
    margin-right: -0.625rem;
    margin-bottom: -0.5rem;
    padding: 0.375rem 0.625rem;
    border-radius: 0 0 0.375rem 0.375rem;
  }

  .fav-confirm-text {
    flex: 1;
    font-size: 0.6875rem;
    color: #991b1b;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .fav-confirm-btn {
    padding: 0.1875rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    border-radius: 0.25rem;
    cursor: pointer;
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
    white-space: nowrap;
  }

  .fav-confirm-btn:hover {
    background: #f3f4f6;
  }

  .fav-confirm-btn.danger {
    background: #dc2626;
    border-color: #dc2626;
    color: white;
  }

  .fav-confirm-btn.danger:hover {
    background: #b91c1c;
  }

  .fav-action-btn.danger:hover {
    color: #dc2626;
    background: #fef2f2;
    border-color: #fca5a5;
  }

  /* Edit form */
  .fav-edit-form {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .fav-edit-row {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .icon-picker-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 0.125rem;
  }

  .icon-option-sm {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    font-size: 0.625rem;
    background: transparent;
    border: 1px solid #e5e7eb;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.1s;
    padding: 0;
  }

  .icon-option-sm:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .icon-option-sm.selected {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .fav-edit-input {
    flex: 1;
    padding: 0.25rem 0.5rem;
    font-size: 0.8125rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    outline: none;
  }

  .fav-edit-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  .fav-edit-save,
  .fav-edit-cancel {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 0.25rem;
    cursor: pointer;
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
  }

  .fav-edit-save {
    background: #3b82f6;
    border-color: #3b82f6;
    color: white;
  }

  .fav-edit-save:hover {
    background: #2563eb;
  }

  .fav-edit-cancel:hover {
    background: #f3f4f6;
  }

  /* View toggles */
  .view-toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .view-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.8125rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.15s;
    user-select: none;
  }

  .view-toggle:hover:not(.view-toggle-disabled) {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .view-toggle-selected {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .view-toggle-selected:hover {
    background: #dbeafe;
    border-color: #2563eb;
  }

  .view-toggle-disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .view-toggle-check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    font-size: 0.625rem;
    font-weight: 700;
    color: white;
    background: #3b82f6;
    border-radius: 0.1875rem;
    flex-shrink: 0;
  }

  .view-toggle-check.empty {
    background: transparent;
    border: 1.5px solid #d1d5db;
  }

  .view-toggle-icon {
    font-size: 0.8125rem;
  }

  .view-toggle-label {
    font-size: 0.8125rem;
    color: #374151;
    font-weight: 500;
  }

  .view-count-hint {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-bottom: 0.5rem;
  }

  .navbar-warning {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 0.375rem;
    margin-bottom: 0.75rem;
    animation: fadeIn 0.15s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .navbar-warning-icon {
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  .navbar-warning-text {
    font-size: 0.8125rem;
    color: #92400e;
    font-weight: 500;
  }

  /* Navbar Preview */
  .navbar-preview {
    margin-top: 0.75rem;
  }

  .preview-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 0.375rem;
  }

  .preview-bar {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    overflow-x: auto;
  }

  .preview-view-btns {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.125rem;
    background: #f3f4f6;
    border-radius: 0.25rem;
  }

  .preview-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.125rem 0.375rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border-radius: 0.1875rem;
    white-space: nowrap;
  }

  /* === API Keys Section === */

  .token-reveal {
    background: #ecfdf5;
    border: 1px solid #6ee7b7;
    border-radius: 0.5rem;
    padding: 0.875rem;
    margin-bottom: 1rem;
  }

  .token-reveal-header {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.8125rem;
    color: #065f46;
    margin-bottom: 0.5rem;
  }

  .token-reveal-icon {
    font-size: 0.875rem;
  }

  .token-reveal-body {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .token-value {
    flex: 1;
    padding: 0.375rem 0.625rem;
    font-size: 0.75rem;
    font-family: monospace;
    background: white;
    border: 1px solid #a7f3d0;
    border-radius: 0.375rem;
    color: #065f46;
    word-break: break-all;
    user-select: all;
  }

  .token-highlight {
    background: #d1fae5;
    border-color: #34d399;
    transition: background 0.2s ease;
  }

  .copy-btn-wrapper {
    position: relative;
    flex-shrink: 0;
  }

  .copy-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.3125rem;
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #065f46;
    background: white;
    border: 1px solid #6ee7b7;
    border-radius: 0.375rem;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s;
  }

  .copy-btn:hover {
    background: #d1fae5;
  }

  .copy-btn-success {
    background: #059669;
    border-color: #059669;
    color: white;
  }

  .copy-btn-success:hover {
    background: #047857;
  }

  .copy-toast {
    position: absolute;
    top: -2rem;
    left: 50%;
    transform: translateX(-50%);
    padding: 0.25rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: white;
    background: #065f46;
    border-radius: 0.25rem;
    white-space: nowrap;
    pointer-events: none;
    animation: toastIn 0.2s ease-out;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .copy-toast::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid #065f46;
  }

  @keyframes toastIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  .token-warning {
    font-size: 0.75rem;
    color: #92400e;
    font-weight: 500;
  }

  .create-key-form {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .key-name-input {
    /* Wraps the button onto its own line when there's no room for both */
    flex: 1 1 12rem;
    min-width: 0;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    outline: none;
    transition: border-color 0.15s;
  }

  .key-name-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }

  .create-key-btn {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;
  }

  .create-key-btn:hover:not(:disabled) {
    background: #2563eb;
  }

  .create-key-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .api-keys-list {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .api-keys-header {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  .api-key-row {
    display: flex;
    align-items: center;
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .api-key-row:last-child {
    border-bottom: none;
  }

  .ak-col-name {
    flex: 2;
    min-width: 0;
  }

  .ak-col-created,
  .ak-col-used {
    flex: 1.5;
    min-width: 0;
  }

  .ak-col-action {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    min-width: 0;
  }

  .ak-name {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ak-date {
    font-size: 0.75rem;
    color: #6b7280;
  }

  .revoke-btn {
    padding: 0.25rem 0.625rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #dc2626;
    background: transparent;
    border: 1px solid #fca5a5;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .revoke-btn:hover {
    background: #fef2f2;
    border-color: #dc2626;
  }

  .ak-time-ago {
    display: block;
    font-size: 0.6875rem;
    color: #9ca3af;
  }

  /* Revoke Confirmation Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    animation: overlay-in 150ms ease-out both;
  }

  .modal-overlay.closing {
    animation: overlay-out 100ms ease-in both;
  }

  .modal-overlay.closing .modal-card {
    animation: card-out 100ms ease-in both;
  }

  .modal-card {
    background: white;
    border-radius: 0.75rem;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 28rem;
    margin: 0 1rem;
    overflow: hidden;
    animation: card-in 150ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .modal-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1.25rem 1.5rem 0;
  }

  .modal-header-icon {
    font-size: 1.25rem;
  }

  .modal-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
  }

  .modal-body {
    padding: 1rem 1.5rem 1.25rem;
  }

  .modal-key-name {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.75rem;
  }

  .modal-last-used {
    font-size: 0.8125rem;
    color: #6b7280;
    margin-bottom: 0.75rem;
  }

  .modal-text {
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.5;
    margin-bottom: 0.75rem;
  }

  .modal-hint {
    color: #6b7280;
    font-size: 0.8125rem;
    margin-bottom: 0;
  }

  .modal-implications {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    margin-bottom: 0.75rem;
  }

  .modal-implications-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #991b1b;
    margin-bottom: 0.375rem;
  }

  .modal-implications-list {
    list-style: disc;
    padding-left: 1.25rem;
    margin: 0;
  }

  .modal-implications-list li {
    font-size: 0.8125rem;
    color: #7f1d1d;
    line-height: 1.6;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .modal-btn {
    padding: 0.5rem 1.125rem;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .modal-btn-cancel {
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;
  }

  .modal-btn-cancel:hover {
    background: #f3f4f6;
  }

  .modal-btn-danger {
    background: #dc2626;
    border: 1px solid #dc2626;
    color: white;
  }

  .modal-btn-danger:hover {
    background: #b91c1c;
  }

  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes card-in {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes card-out {
    from {
      transform: scale(1);
      opacity: 1;
    }
    to {
      transform: scale(0.95);
      opacity: 0;
    }
  }

  .preview-fav {
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 0.25rem;
    padding: 0.125rem 0.375rem;
    margin-right: 0.25rem;
  }

  /* === Mobile (<768px): 16px gutters === */
  @media (max-width: 767px) {
    .settings-page {
      padding: 1rem;
    }

    .settings-title {
      margin-bottom: 1rem;
    }

    .settings-section {
      padding: 1rem;
      margin-bottom: 1rem;
    }

    /* Input on its own line, button right-aligned like "Save Changes" */
    .key-name-input {
      flex-basis: 100%;
    }

    .create-key-btn {
      margin-left: auto;
    }

    .field-select,
    .save-btn,
    .now-refill {
      min-height: var(--mobile-tap);
    }

    .now-refill input {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
</style>
